/* eslint-disable react/jsx-no-bind */
import React from 'react';
import styled from 'styled-components';
import { breakpointsMap, ThemeProvider } from '@oneaudi/unified-web-common';
import { Divider, TextButton, Layout, LayoutItem, Headline } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import mapContext from '../../../contexts/map-context';
import { messages } from '../../../i18n/messages';
import { ActionTypes } from '../../../contexts/reducers/map-reducer';
import SpacingContainer from '../../common/SpacingContainer/SpacingContainer';
import Filters from '../Filters/Filters';
import { ReactComponent as CancelIcon } from '../../../icons/cancel-small.svg';
import trackingContext from '../../../contexts/tracking-context';
import { trackFilterCancelAction } from '../../../utils/trackingUtils';

export default function FiltersOverlay() {
  const { results, userSelectedFilters } = mapContext.useMapState();
  const dispatch = mapContext.useMapDispatch();
  const initialSelectedFilters = React.useRef([...userSelectedFilters]);
  const { trackingService: tService } = trackingContext.useTrackingState();
  const btnLabel = getButtonLabel(results?.length);

  function getButtonLabel(resultCount: number | undefined) {
    let label = messages.filter_show_no_result;
    if (resultCount) {
      if (resultCount === 1) {
        label = messages.filter_show_one_result;
      }
      if (resultCount > 1) {
        label = messages.filter_show_big_results;
      }
    }
    return useI18n(label, { resultCount: resultCount || '' });
  }

  function applyFilters() {
    dispatch({ type: ActionTypes.setIsFiltersOverlayOpen, payload: false });
  }

  function handleOverlayClose() {
    if (JSON.stringify(initialSelectedFilters.current) !== JSON.stringify(userSelectedFilters)) {
      trackFilterCancelAction(tService, 'FiltersOverlay', window.location.href);
    }
    dispatch({ type: ActionTypes.setFilters, payload: initialSelectedFilters.current });
    dispatch({ type: ActionTypes.setIsFiltersOverlayOpen, payload: false });
  }

  return (
    <ThemeProvider theme={{ name: 'dark' }}>
      <OverlayContainer data-test-id="filters-overlay-component">
        <Layout margin={['24px', '28px', '24px', '28px']} justify="space-between">
          <LayoutItem align="center">
            <Headline tag="h2" variant={{ order: '4', type: 'headline', weight: 'normal' }}>
              {useI18n(messages.filter)}
            </Headline>
          </LayoutItem>
          <LayoutItem align="center">
            <CloseButton
              onClick={handleOverlayClose}
              data-test-id="close-button"
              aria-label="close filters"
            >
              <CancelIcon />
            </CloseButton>
          </LayoutItem>
        </Layout>
        <StyledDivider />
        <SpacingContainer style={{ padding: '40px 28px' }}>
          <Filters />
        </SpacingContainer>
        <ButtonContainer>
          <StyledDivider />
          <SpacingContainer style={{ padding: '24px 28px' }}>
            <ButtonDarkMode
              variant="secondary"
              isFullWidth
              onClick={applyFilters}
              data-test-id="submit-button"
              disabled={!results || results.length < 1}
            >
              {btnLabel}
            </ButtonDarkMode>
          </SpacingContainer>
        </ButtonContainer>
      </OverlayContainer>
    </ThemeProvider>
  );
}

const OverlayContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: #000;

  @media (max-width: ${breakpointsMap.md}) {
    position: fixed;
    z-index: 10000;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }
`;

const CloseButton = styled.button`
  border: 0;
  background: transparent;
  color: #fff;
  cursor: pointer;
  margin: 0;
  padding: 0;
  line-height: 0;
`;

const StyledDivider = styled(Divider)`
  border-color: #fff;
`;

const ButtonContainer = styled.div`
  bottom: 0;
  left: 0;
  right: 0;
`;

const ButtonDarkMode = styled(TextButton)`
  :disabled {
    color: #666;
    border: 1px solid #666;
  }
`;
